import React, { useState, useContext } from "react";
import { handleCreateAlbum } from "../API/api";
import { EventType, Album } from "./types";
import {
  validateName,
  restrictedTermsError
} from "../../../utils/validateName";
import { PhotoManagerContext } from ".";
import {
  decodeEntityPath,
  getEntityInfoFromPath
} from "./FolderFeatures/EncryptionOfFolder";

interface NewAlbumModalProps {
  eventId: number;
  onClose: () => void;
  onCreateAlbumSuccess: (newAlbum: Album) => void;
}

const NewAlbumModal: React.FC<NewAlbumModalProps> = ({
  eventId,
  onClose,
  onCreateAlbumSuccess
}) => {
  const [albumName, setAlbumName] = useState("");
  const [groupPhotos, setGroupPhotos] = useState(false);
  const [invalidName, setInvalidName] = useState(false);
  const context = useContext(PhotoManagerContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const pathParam = new URLSearchParams(window.location.search).get("p");

    // Use the eventId passed from the sidebar if available, otherwise fall back to path validation
    if (eventId && eventId > 0) {
      try {
        // Find the event data from context to check if it's a root event
        const eventData = context?.eventsList?.find(
          event => event.id === eventId
        );

        // Use context.encodedString if pathParam is null or invalid
        const encodedPathToUse = pathParam || context?.encodedString || "";
        const newAlbum = await handleCreateAlbum(
          albumName,
          groupPhotos,
          eventId,
          undefined, // Not a reference when creating from sidebar
          context?.groupPhotosEnabled,
          encodedPathToUse,
          context?.handleURLChange,
          onClose,
          eventData, // Pass event data to determine if root event
          context
        );
        onCreateAlbumSuccess(newAlbum);
      } catch (error) {
        console.error("Error creating album:", error);
      }
      return;
    }

    // Fallback to original path validation for other cases
    if (!pathParam) {
      //@ts-ignore
      toastr.error("Missing path in URL");
      return;
    }

    const decodedPath = decodeEntityPath(pathParam);
    const { type, id: pathEventId } = getEntityInfoFromPath(decodedPath);

    if (!pathEventId || (type !== "gallery" && type !== "reference")) {
      //@ts-ignore
      toastr.error("Invalid event context to create album");
      return;
    }

    try {
      // Find the event data from context for fallback case too
      const eventData = context?.eventsList?.find(
        event => event.id === pathEventId
      );

      const newAlbum = await handleCreateAlbum(
        albumName,
        groupPhotos,
        pathEventId,
        type === "reference" ? "referenceId" : undefined,
        context?.groupPhotosEnabled,
        pathParam,
        context?.handleURLChange,
        onClose,
        eventData, // Pass event data to determine if root event
        context // Pass context to access eventsList
      );
      // Call success callback after successful creation
      onCreateAlbumSuccess(newAlbum);
    } catch (error) {
      console.error("Error creating album:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAlbumName(value);
    const isValidName = validateName(value);
    if (!isValidName) {
      setInvalidName(true);
    } else {
      setInvalidName(false);
    }
  };

  const showActionTooltip = () => {
    $(`[data-toggle="tooltip"]#group-pic-info-icon`).tooltip("show");
  };

  return (
    <div>
      <div
        id="filter-dialog"
        className="modal fade in geo-modal "
        role="dialog"
      >
        <div className="modal-dialog filter-dialog-content ws-create-album modal-md">
          <div className="modal-header">
            <button
              type="button"
              className="close"
              onClick={onClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-content">
            <div className="new-album-modal-content">
              <h3>Provide the name for your new album</h3>
              <div className="container-fluid">
                <form
                  onSubmit={(e: React.FormEvent<Element>) => handleSubmit(e)}
                >
                  <input
                    type="hidden"
                    name="user_id"
                    value={context ? context.currentUserId : -1}
                  />
                  <input type="hidden" name="event_id" value={eventId} />
                  <div className="row geo geo-form geo-form--fw">
                    <div className="col-md-10 col-md-offset-1 form">
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your album name"
                        id="album_name"
                        className={`geo-form__input ws-geo-input ${
                          invalidName && "ws-new-album-name-input-error"
                        }`}
                        value={albumName}
                        onChange={e => {
                          handleChange(e);
                        }}
                      />
                      {invalidName && (
                        <small className="error name-error">
                          {restrictedTermsError}
                        </small>
                      )}
                      {context?.groupPhotosEnabled && (
                        <div className="geo-grp-photos">
                          <label className="switch geo-form__toggle">
                            <input
                              type="checkbox"
                              className="geo-form__toginput"
                              checked={groupPhotos}
                              onChange={e => setGroupPhotos(e.target.checked)}
                            />
                            <span className="geo-form__tog">
                              <span className="geo-form__tog-in"></span>
                            </span>
                            <span className="geo-form__togtext">
                              This album is for large group photos
                              <br />
                              (100+ people){" "}
                              <i
                                id="group-pic-info-icon"
                                className="fa fa-info-circle"
                                data-toggle="tooltip"
                                data-placement="right"
                                data-original-title="Select this option only for large groups, otherwise processing may be impacted"
                                onMouseEnter={() => showActionTooltip()}
                              ></i>
                            </span>
                          </label>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="geo-btn geo-btn--secondary geo-btn--caps create-album-btn"
                        data-disable-with="Creating..."
                        disabled={invalidName}
                      >
                        Create album
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </div>
  );
};

export default NewAlbumModal;
